import {gql} from '@apollo/client';
import React, {useEffect} from 'react';

import {setWindowTitle} from 'BenchlingUtil';
import ChemicalCounterpartLibrary from 'chemistry/ChemicalCounterpartLibrary';
import {
  archiveSolvents,
  createSolvent,
  SOLVENT_CREATE_FROM_CSV_URL,
  unarchiveSolvents,
  updateSolvent,
} from 'chemistry/ChemistryDAO';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {ErrorFragment} from 'graphql-client/Error';
import {TABLE_KEYS} from 'ui-library/scrollable/ScrollableTableConstants';
import {withLoadingScreen} from 'util/Promises';

import {SolventLibraryQuery} from './__generated/SolventLibraryQuery';

export const SOLVENT_LIBRARY_QUERY = gql`
  query SolventLibraryQuery @skipAudit @engComponent(name: CHEMISTRY) {
    solvents {
      id
      name
      code
      calculatedMolecularWeight
      canonicalSmiles
      archiveData {
        archived
        purpose
      }
      imageData {
        ... on SVGNode {
          base64Svg
        }
        ...Error
      }
    }
  }
  ${ErrorFragment.fragment}
`;

export default function SolventLibrary(): JSX.Element {
  useEffect(() => {
    setWindowTitle('Solvents');
  }, []);

  const result = useBenchlingQuery<SolventLibraryQuery>(SOLVENT_LIBRARY_QUERY);
  return (
    <ChemicalCounterpartLibrary
      counterpartName="solvent"
      items={result.type === QueryResultType.SUCCESS ? result.data.solvents : []}
      loading={result.type === QueryResultType.PENDING}
      tableKey={TABLE_KEYS.SOLVENT_LIBRARY_TABLE}
      importFromCSVUrl={SOLVENT_CREATE_FROM_CSV_URL}
      onAddNew={async (name: string, code: string, sketcherValue: string) => {
        await createSolvent(name, sketcherValue, code);
        // Refetch GQL query to include new solvent in the results.
        await result.refetch();
      }}
      onEdit={async (id: string, name: string, code: string) => {
        await updateSolvent(id, name, code);
        await result.refetch();
      }}
      onArchive={async (solventIds, purpose) => {
        await withLoadingScreen(archiveSolvents(solventIds, purpose));
        await result.refetch();
      }}
      onUnarchive={async (solventIds) => {
        await withLoadingScreen(unarchiveSolvents(solventIds));
        await result.refetch();
      }}
    />
  );
}
